<template>
  <div v-if="store.state.isUserLoggedIn" class="mt-10">
    <div class="d-flex align-center ga-4 mb-4">
      <v-text-field
        v-model="tagSearch"
        placeholder="Search by tag..."
        prepend-inner-icon="mdi-magnify"
        density="compact"
        variant="outlined"
        clearable
        hide-details
        style="max-width: 300px;"
      />
      <v-checkbox
        v-model="showTaggedOnly"
        label="Show tagged only"
        density="compact"
        hide-details
        class="flex-grow-0"
      />
      <v-progress-circular
        v-if="isLoadingAllHistory"
        indeterminate
        size="20"
        width="2"
        color="primary"
      />
    </div>

    <v-data-table
      :headers="headers"
      :items="filteredVersions"
      :items-per-page="-1"
      disable-pagination
      hide-default-footer
    >
      <template v-slot:bottom></template>
      <template v-slot:item.current="{ item }">
        <v-icon v-if="isCurrentVersion(item)">mds:check</v-icon>
      </template>
      <template v-slot:item.tag="{ item }">
        <div v-if="!isCurrentVersion(item)" class="d-flex align-center">
          <div v-if="item.tag" class="tag-display" @click="openTagDialog(item)">
            <v-icon size="x-small" color="primary" class="mr-1">mdi-tag</v-icon>
            <span class="tag-name">{{ item.tag }}</span>
            <v-icon size="x-small" color="grey-darken-1" class="edit-icon ml-1">mdi-pencil</v-icon>
          </div>
          <div
            v-else
            class="add-tag-icon"
            @click="openTagDialog(item)"
            :class="{ 'disabled': store.state.isWorkflowDisabled }"
          >
            <v-icon size="small" color="grey-darken-1">mdi-plus</v-icon>
            <v-tooltip location="top" activator="parent">
              Add tag
            </v-tooltip>
          </div>
        </div>
      </template>
      <template v-slot:item.version="{ item }">
        <a
          v-if="!isCurrentVersion(item)"
          href="#"
          class="version-link"
          @click.prevent="openVersionInNewTab(item.version)"
          :title="`View version ${item.version}`"
        >
          {{ item.version }}
        </a>
        <span v-else class="current-version">
          {{ item.version }}
        </span>
      </template>
      <template v-slot:item.action="{ item }">
        <div class="d-flex ga-1 justify-end">
          <!-- Public/anonymous sharing DISABLED (2026-06): authenticated-only platform.
               Per-version "shareable link" hidden; preserved for a future admin-gated feature. -->
          <v-btn
            v-if="false"
            icon="mdi-link-variant"
            size="x-small"
            variant="text"
            @click="copyVersionLink(item.version)"
            :disabled="workflow.accessLevel !== 'READ'"
          >
            <v-tooltip location="top" activator="parent">
              {{ workflow.accessLevel === 'READ' ? 'Copy shareable link' : 'Enable public access to share' }}
            </v-tooltip>
          </v-btn>
          <div
            v-if="!isCurrentVersion(item) && store.state.workflow.isViewOnlyEnabled === false"
            class="v-btn v-btn--size-small v-btn--variant-flat v-btn--density-default cursor-pointer"
            :class="{ 'v-btn--disabled': store.state.isWorkflowDisabled }"
            @click="restoreVersion(item.version)"
          >
            <span class="v-btn__overlay"></span>
            <span class="v-btn__content">
              Restore
            </span>
          </div>
        </div>
      </template>
      <template v-slot:item.updatedAt="{ item }">
        {{ item.updatedAt ? formatDistanceToNowStrict(new Date(item.updatedAt), { addSuffix: true }) : '' }}
      </template>
      <template v-slot:item.updateNote="{ item }">
        <span class="emphasize">{{ item.updateNote?.userRealName }}</span>
        {{ item.updateNote?.action }}
        <span class="emphasize">{{ item.targetText }}</span>
      </template>
    </v-data-table>

    <div v-if="showLoadMoreSentinel" ref="historySentinel" class="d-flex justify-center pa-4">
      <v-progress-circular indeterminate size="36" color="primary" />
    </div>

    <!-- Tag Dialog -->
    <v-dialog v-model="tagDialog" max-width="400">
      <v-card>
        <v-card-title class="d-flex align-center py-3">
          <v-icon class="mr-2" size="small">mdi-tag</v-icon>
          <span class="text-subtitle-1">Tag Version {{ selectedVersion?.version }}</span>
        </v-card-title>
        <v-card-text class="py-2">
          <v-text-field
            v-model="tagInput"
            label="Tag name"
            variant="outlined"
            density="compact"
            :rules="tagRules"
            autofocus
            hide-details="auto"
            @keydown.enter="saveTag"
          >
            <template v-slot:prepend-inner>
              <v-icon size="small">mdi-tag-outline</v-icon>
            </template>
          </v-text-field>
        </v-card-text>
        <v-card-actions class="py-1">
          <v-btn
            v-if="selectedVersion?.tag"
            color="red"
            variant="tonal"
            @click="removeTag"
          >
            Remove
          </v-btn>
          <v-spacer/>
          <v-btn variant="tonal" @click="closeTagDialog">Cancel</v-btn>
          <v-btn
            color="green"
            variant="tonal"
            @click="saveTag"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
  <div v-else class="history-view-mode">
    <v-icon color="white" >info</v-icon>
    <span>You need to <a href="/login">log in</a> to see Workflow history</span>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import workflowApi from '@/api/workflowApi'
import { htmlToText } from '@/utils/sanitize'
import formatDistanceToNowStrict from 'date-fns/formatDistanceToNowStrict'

const { workflow, versions } = defineProps({
  workflow: Object,
  versions: Array
})

const store = useStore()

const isLoadingMore = ref(false)
const observer = ref(null)
const historySentinel = ref(null)

// Tag management state
const tagDialog = ref(false)
const tagInput = ref('')
const selectedVersion = ref(null)
const tagSearch = ref('')
const showTaggedOnly = ref(false)
const isLoadingAllHistory = ref(false)

const headers = [
  {
    title: 'Current',
    value: 'current',
    width: '100px',
    sortable: false
  },
  {
    title: 'When',
    value: 'updatedAt',
    width: '200px',
    sortable: false
  },
  {
    title: 'Description',
    align: 'start',
    sortable: false,
    value: 'updateNote'
  },
  {
    title: 'Tag',
    value: 'tag',
    width: '180px',
    align: 'start',
    sortable: false
  },
  {
    title: 'Version',
    value: 'version',
    width: '80px',
    align: 'end',
    sortable: false
  },
  {
    title: 'Action',
    value: 'action',
    align: 'end',
    width: '120px',
    sortable: false
  }
]

// Tag validation rules
const tagRules = [
  (v) => {
    if (!v || !v.trim()) return true // Allow empty to remove tag
    const pattern = /^[a-zA-Z0-9\s\-_.]*$/
    return pattern.test(v) || 'Tag can only contain letters, numbers, spaces, hyphens, underscores, and dots'
  }
]

const nextToken = computed(() => store.state.workflowHistory.nextToken)
const taggedHistory = computed(() => store.state.workflowHistory.taggedHistory)
const taggedNextToken = computed(() => store.state.workflowHistory.taggedNextToken)

const isCurrentVersion = (item) => item.version === workflow.version

const isTagValid = computed(() => {
  // Allow empty (to remove tag)
  if (!tagInput.value || !tagInput.value.trim()) return true
  const pattern = /^[a-zA-Z0-9\s\-_.]*$/
  return pattern.test(tagInput.value)
})

const showLoadMoreSentinel = computed(() => {
  if (showTaggedOnly.value) {
    return taggedNextToken.value && !tagSearch.value?.trim()
  }
  return nextToken.value && !showTaggedOnly.value && !tagSearch.value?.trim()
})

const filteredVersions = computed(() => {
  const hasSearchTerm = tagSearch.value && tagSearch.value.trim().length > 0

  // When showing tagged only or searching, use taggedHistory
  let result
  if (showTaggedOnly.value || hasSearchTerm) {
    result = taggedHistory.value || []
    if (hasSearchTerm) {
      const searchLower = tagSearch.value.toLowerCase()
      result = result.filter((v) => v.tag?.toLowerCase().includes(searchLower))
    }
  } else {
    result = versions || []
  }

  return result.map((item) => ({
    ...item,
    targetText: htmlToText(item.updateNote?.target)
  }))
})

const loadTaggedHistory = async () => {
  if (isLoadingAllHistory.value) {
    return
  }

  isLoadingAllHistory.value = true

  try {
    await store.dispatch('workflowHistory/listTaggedHistory', {
      projectId: workflow.projectId,
      workflowId: workflow.id
    })
  } catch (error) {
    store.commit('snackbar/showMessage', {
      content: error.message || 'Error loading tagged history',
      timeout: 6000,
      color: 'red'
    }, { root: true })
  } finally {
    isLoadingAllHistory.value = false
  }
}

watch(showTaggedOnly, (newValue) => {
  if (newValue && !taggedHistory.value) {
    loadTaggedHistory()
  }
})

watch(tagSearch, (newValue) => {
  if (newValue && newValue.trim() && !taggedHistory.value) {
    loadTaggedHistory()
  }
})

const openTagDialog = (item) => {
  selectedVersion.value = item
  tagInput.value = item.tag || ''
  tagDialog.value = true
}

const closeTagDialog = () => {
  tagDialog.value = false
  selectedVersion.value = null
  tagInput.value = ''
}

const saveTag = async () => {
  if (!isTagValid.value || !selectedVersion.value) return

  const trimmedTag = tagInput.value.trim()

  // If empty, remove the tag
  if (!trimmedTag) {
    removeTag()
    return
  }

  try {
    await store.dispatch('workflowHistory/tagWorkflowVersion', {
      projectId: workflow.projectId,
      workflowId: workflow.id,
      version: selectedVersion.value.version,
      tag: trimmedTag
    })

    store.commit('snackbar/showMessage', {
      content: `Tag "${trimmedTag}" saved for version ${selectedVersion.value.version}`,
      timeout: 3000
    }, { root: true })

    closeTagDialog()
  } catch (error) {
    store.commit('snackbar/showMessage', {
      content: error.message || 'Error saving tag',
      timeout: 6000,
      color: 'red'
    }, { root: true })
  }
}

const removeTag = async () => {
  if (!selectedVersion.value) return

  try {
    await store.dispatch('workflowHistory/tagWorkflowVersion', {
      projectId: workflow.projectId,
      workflowId: workflow.id,
      version: selectedVersion.value.version,
      tag: null
    })

    store.commit('snackbar/showMessage', {
      content: `Tag removed from version ${selectedVersion.value.version}`,
      timeout: 3000
    }, { root: true })

    closeTagDialog()
  } catch (error) {
    store.commit('snackbar/showMessage', {
      content: error.message || 'Error removing tag',
      timeout: 6000,
      color: 'red'
    }, { root: true })
  }
}

const openVersionInNewTab = (version) => {
  const url = `${window.location.origin}/workflow/${workflow.projectId}/${workflow.id}/version/${version}`
  window.open(url, '_blank')
}

const copyVersionLink = (version) => {
  const url = `${window.location.origin}/workflow/${workflow.projectId}/${workflow.id}/version/${version}`
  navigator.clipboard.writeText(url)
  store.commit('snackbar/showMessage', {
    content: 'Version link copied to clipboard!',
    timeout: 3000
  }, { root: true })
}

const restoreVersion = (version) => {
  workflowApi.restorePreviousWorkflowVersion(version, workflow)
}

const setupObserver = () => {
  if (observer.value) {
    observer.value.disconnect()
  }

  observer.value = new IntersectionObserver((entries) => {
    const [entry] = entries
    if (entry.isIntersecting && !isLoadingMore.value && showLoadMoreSentinel.value) {
      loadMore()
    }
  })

  if (historySentinel.value) {
    observer.value.observe(historySentinel.value)
  }
}

const loadMore = async () => {
  if (isLoadingMore.value) {
    return
  }

  if (showTaggedOnly.value) {
    if (!taggedNextToken.value) return

    isLoadingMore.value = true

    try {
      await store.dispatch('workflowHistory/listTaggedHistory', {
        projectId: workflow.projectId,
        workflowId: workflow.id,
        nextToken: taggedNextToken.value
      })
    } catch (error) {
      store.commit('snackbar/showMessage', {
        content: error.message || 'Error loading more tagged history',
        timeout: 6000,
        color: 'red'
      }, { root: true })
    } finally {
      isLoadingMore.value = false
    }
  } else {
    if (!nextToken.value) return

    isLoadingMore.value = true

    try {
      await store.dispatch('workflowHistory/listWorkflowHistory', {
        projectId: workflow.projectId,
        workflowId: workflow.id,
        nextToken: nextToken.value,
        subscribe: false
      })
    } catch (error) {
      store.commit('snackbar/showMessage', {
        content: error.message || 'Error loading more workflow history',
        timeout: 6000,
        color: 'red'
      }, { root: true })
    } finally {
      isLoadingMore.value = false
    }
  }
}

watch(historySentinel, (newValue, oldValue) => {
  if (oldValue && observer.value) {
    observer.value.unobserve(oldValue)
  }
  if (newValue) {
    setupObserver()
  }
})

onMounted(() => {
  if (historySentinel.value) {
    setupObserver()
  }
})

onBeforeUnmount(() => {
  if (observer.value) {
    observer.value.disconnect()
  }
})
</script>

<style scoped>
.emphasize {
  font-weight: 700;
}

.hidden {
  display: none;
}

.search-field {
  align-self: center;
}

.history-view-mode {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 10px;
  padding: 5px;
  border-radius: 3px;
  background: #4C83D1;
  color: #ffffff;
}

.history-view-mode span {
  margin-left: 5px;
}

.tag-display {
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  transition: background-color 0.2s;
}

.tag-display:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.tag-display .edit-icon {
  opacity: 0;
  transition: opacity 0.2s;
}

.tag-display:hover .edit-icon {
  opacity: 1;
}

.tag-name {
  font-size: 0.875rem;
  color: #1976d2;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.add-tag-icon {
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.add-tag-icon:hover {
  background-color: rgba(0, 0, 0, 0.05);
  transform: scale(1.1);
}

.add-tag-icon.disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.version-link {
  color: #1976d2;
  text-decoration: none;
  cursor: pointer;
  font-weight: 500;
}

.version-link:hover {
  text-decoration: underline;
}

.current-version {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
}

[data-theme="dark"] .current-version {
  color: var(--text-light);
}
</style>
